import ReactQuill from "react-quill";
import styled, { css } from "styled-components";
import { rgba } from "polished";

import {
  borderRadius,
  colors,
  fontStyle,
  space,
  boxShadow,
  fontSize,
  zIndex,
  breakpoint,
} from "shared/styles/constants";
import { defaultLink, getStatus, hoverFocus } from "shared/styles/defaults";
import { IStatus } from "shared/types";

interface RTEContainerProps {
  error?: boolean;
  size?: "s" | "m";
}

const RTEContentStyle = css`
  strong {
    ${fontStyle.bold}
  }

  em {
    ${fontStyle.italic}
  }

  ul,
  ol {
    margin: ${space.xxs} 0;
    padding-left: ${space.m};
  }

  ul {
    list-style: disc;
  }

  ol {
    list-style: decimal;
  }

  a {
    ${defaultLink()}
  }
`;

export const StyledRTEContent = styled.div`
  ${RTEContentStyle}

  li + li {
    margin-top: ${space.xxs};
  }

  @media print {
    color: ${colors.black};
    padding: ${space.s};
  }
`;

export const StyledReactQuill = styled(ReactQuill)`
  border-radius: ${borderRadius.xs};
`;

export const RTEContainer = styled.div<RTEContainerProps>`
  background-color: ${colors.white};
  border-radius: ${borderRadius.xs};

  ${({ error }) =>
    error &&
    css`
      box-shadow: 0 0 0 2px ${colors.red};
    `};

  .ql-container {
    border-radius: 0 0 ${borderRadius.xs} ${borderRadius.xs};
  }

  .ql-editor {
    color: ${colors.black};
    ${fontStyle.regular};
    ${fontSize.base};

    padding: ${space.s};
    min-height: ${({ size }) => (size === "s" ? "120px" : "200px")};

    ${breakpoint("s", "down")} {
      /* 
        we do this to fix an android bug 
        https://git.wieni.be/wieni/tourmanagement/app.tourmanagement.com/-/issues/595#note_326823
      */
      padding: ${space.l} ${space.s};
    }

    ${RTEContentStyle}

    ul, ol {
      padding-left: 0;
    }

    a {
      ${defaultLink(colors.blue, colors.blueLight)}
      text-decoration: none !important;
    }
  }

  .ql-clipboard,
  .ql-hidden,
  .ql-preview {
    display: none;
  }

  .ql-tooltip {
    background-color: ${colors.grey2} !important;
    color: ${colors.white} !important;

    padding: ${space.s} !important;
    border-radius: ${borderRadius.s} !important;
    box-shadow: ${boxShadow.s} !important;
    border: none !important;
    z-index: ${zIndex.above};

    &[data-mode="link"]::before {
      content: "url" !important;
    }

    &::before {
      ${fontStyle.bold}
    }

    a.ql-preview,
    a.ql-remove:before,
    a.ql-action:after {
      ${defaultLink()}
    }

    a.ql-action:after {
      border-right: 0;
      margin-right: ${space.xxs};
      padding-right: 0;

      color: ${getStatus(IStatus.success).textColor};
    }

    a.ql-remove:before {
      color: ${getStatus(IStatus.error).textColor};
    }

    input[type="text"] {
      border-radius: ${borderRadius.xs};
      ${fontSize.base};
      padding: ${space.xs};
    }
  }
`;

export const StyledToolbar = styled.div`
  background-color: ${colors.blackLight};
  color: ${colors.white};
  border: none !important;

  padding: ${space.s} !important;
  margin: 0;
`;

export const StyledToolbarButton = styled.button`
  width: 32px !important;
  height: 32px !important;
  padding: 0 !important;

  display: flex !important;
  justify-content: center !important;
  align-items: center !important;

  // theme
  background-color: ${rgba(colors.white, 0.85)} !important;
  color: ${colors.black} !important;
  border: 1px solid ${rgba(colors.white, 0.85)} !important;
  border-radius: ${borderRadius.s};

  ${hoverFocus.defaultHover(rgba(colors.white, 0.85))};

  &.ql-active {
    background-color: ${colors.white} !important;
    border: 1px solid ${colors.white} !important;

    box-shadow: none !important;

    ${hoverFocus.defaultHover(colors.white)};
  }
`;

export const StyledToolbarSelect = styled.select`
  width: 32px !important;
  height: 32px !important;
  padding: 0 !important;

  display: flex !important;
  justify-content: center !important;
  align-items: center !important;

  // theme
  background-color: ${rgba(colors.white, 0.85)} !important;
  color: ${colors.black} !important;
  border: 1px solid ${rgba(colors.white, 0.85)} !important;
  border-radius: ${borderRadius.s};

  ${hoverFocus.defaultHover(rgba(colors.white, 0.85))};

  &.ql-active {
    background-color: ${colors.white} !important;
    border: 1px solid ${colors.white} !important;

    box-shadow: none !important;

    ${hoverFocus.defaultHover(colors.white)};
  }
`;
